<script setup lang="ts">
import { computed, ref, watch } from "vue";
import "@maersk-global/mds-components-core/mc-label";
import "@maersk-global/mds-components-core/mc-radio";
import "@maersk-global/mds-components-core/mc-radio-group";
import { useI18n } from "vue-i18n";
import { useApplyFilter } from "@/features/filters/composables/useApplyFilter/useApplyFilter";
import { useFiltersStore } from "@/store";
import type { FilterDefinition, FilterType } from "@/features/filters/types";
import { FilterTypeEnum } from "@/features/filters/types";

interface Props {
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
}

const props = defineProps<Props>();
const { t } = useI18n();
const filtersStore = useFiltersStore();

const { filtersApplied, panelSelectedFilters } = filtersStore;

const zone = props.zonePrefix ?? "default";
const filterKey = props.filter.key;
const getInitialValue = (): string => {
  const { value, default: d, options } = props.filter;

  return [value, d, options?.[0]?.value].find((v): v is string => typeof v === "string") ?? "";
};

const currentValue = ref<string | number>(getInitialValue());

const { applyFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const selectedFilter = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  return source?.[zone]?.[filterKey];
});

const handleChange = (value: string | number) => {
  currentValue.value = value;

  const selectedOption = props.filter.options?.find((opt) => opt.value === value);

  const payload = {
    label: selectedOption?.label || value,
    value: value,
    filterLabel: props.filter.label
  };

  applyFilter(props.filter.key, payload);
};

// Initialize from existing filter value
if (selectedFilter.value?.value !== undefined) {
  currentValue.value = selectedFilter.value.value as string;
} else if (props.filter.value || props.filter.default) {
  // Apply default value
  handleChange(getInitialValue());
}

watch(
  () => selectedFilter.value,
  (newValue) => {
    if (newValue?.value !== undefined && newValue.value !== currentValue.value) {
      currentValue.value = newValue.value as string;
    }
  }
);
</script>

<template>
  <div class="radio-filter" :data-test="`radio-filter/${filter.key}`">
    <mc-label v-if="!filter.hiddenlabel" class="radio-filter__label" :data-test="`radio-filter/${filter.key}/label`">
      {{ t(filter.label) }}
    </mc-label>

    <mc-radio-group
      v-model="currentValue"
      :hiddenLegend="true"
      class="radio-filter__group"
      :data-test="`radio-filter/${filter.key}/group`"
    >
      <mc-radio
        v-for="option in filter.options"
        :key="option.value"
        :name="filter.key"
        :value="option.value"
        :data-test="`radio-filter/${filter.key}/option/${option.value}`"
        @change="() => handleChange(option.value)"
      >
        <span slot="label">{{ t(option.label) }}</span>
      </mc-radio>
    </mc-radio-group>

    <p
      v-if="filter.description"
      class="radio-filter__description mds-text--x-small"
      :data-test="`radio-filter/${filter.key}/description`"
    >
      {{ t(filter.description) }}
    </p>
  </div>
</template>

<style scoped lang="scss">
@import url("./RadioFilter.scss");
</style>
